import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, X, WifiOff } from 'lucide-react';
import { Spinner } from 'reactstrap';
import Api from 'services/api';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from 'slices/userSlice';
import { getEcho } from 'services/echo';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { setInterpreterIframeUrl, setInterpreterAppid } from 'slices/interpreter/interpreterIframeSlice';

// pusher-js gives up permanently when Pusher refuses a connection (close codes
// 4001-4099, e.g. 4004 "over connection quota"). We retry ourselves on this
// cadence, and while the socket is down we poll the backend so a live call is
// never missed for longer than one interval.
const RECONNECT_INTERVAL_MS = 15000;
const OFFLINE_POLL_INTERVAL_MS = 15000;
const DOWN_STATES = ['disconnected', 'failed', 'unavailable'];

// ─── Single call card ────────────────────────────────────────────────────────
const CallCard = ({ call, onAccept, onReject, loadingId }) => {
  const isLoading = loadingId === call.id;

  return (
    <div style={{
      background: '#1a1a1a',
      borderRadius: 16,
      padding: '16px 18px',
      color: 'white',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      animation: 'vri_slide_in 0.25s ease-out',
      width: 300,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Incoming Video Call
          </div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{call.language}</div>
          {call.appid && (
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>Appointment #{call.appid}</div>
          )}
        </div>
        <button
          onClick={() => onReject(call.id)}
          disabled={isLoading}
          style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 4, lineHeight: 1 }}
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => onReject(call.id)}
          disabled={isLoading}
          style={{
            flex: 1, padding: '10px 0', background: '#dc2626', border: 'none',
            borderRadius: 10, color: 'white', fontWeight: 600, fontSize: 14,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          <PhoneOff size={15} /> Reject
        </button>
        <button
          onClick={() => onAccept(call.id)}
          disabled={isLoading}
          style={{
            flex: 1, padding: '10px 0', background: '#16a34a', border: 'none',
            borderRadius: 10, color: 'white', fontWeight: 600, fontSize: 14,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {isLoading ? <Spinner size="sm" /> : <><Phone size={15} /> Accept</>}
        </button>
      </div>
    </div>
  );
};

// ─── API helper ──────────────────────────────────────────────────────────────
async function acceptVriCall(id) {
  try {
    const { data, status } = await Api.acceptVriCall(id);
    if (status === 200) return { success: true, data };
    return { success: false, message: data?.message };
  } catch (error) {
    return { success: false, message: error?.response?.data?.message };
  }
}

// ─── Container ───────────────────────────────────────────────────────────────
const IncomingCalls = () => {
  const [calls, setCalls] = useState([]);   // newest first
  const [loadingId, setLoadingId] = useState(null);
  const { user } = useSelector(userSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const ringtoneRef = useRef(null);

  const isOnVideoCallPage = location.pathname.includes('join-room-call') || location.pathname.includes('quick-join');

  // When interpreter navigates into the video call page, clear the queue and kill audio.
  // When they come back out, re-check for any call that is still unanswered.
  useEffect(() => {
    if (isOnVideoCallPage) {
      setCalls([]);
    } else {
      syncActiveCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnVideoCallPage]);

  // Single shared audio: play when first call arrives, stop only when queue empties
  useEffect(() => {
    if (calls.length > 0 && !isOnVideoCallPage) {
      if (!ringtoneRef.current) {
        const audio = new Audio('/ringtone.mp3');
        audio.loop = true;
        ringtoneRef.current = audio;
        audio.play().catch(() => {});
      }
    } else {
      const audio = ringtoneRef.current;
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
        ringtoneRef.current = null;
      }
    }
  }, [calls.length, isOnVideoCallPage]);

  // Destroy audio on component unmount
  useEffect(() => {
    return () => {
      const audio = ringtoneRef.current;
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
        ringtoneRef.current = null;
      }
    };
  }, []);

  const isInterpreter = !!user?.role?.includes('interpreter');
  const interpreterUserId = user?.user?.id;
  const [connectionState, setConnectionState] = useState('initialized');

  // Ask the backend for any unanswered on-demand call. Used on mount (late login),
  // after every Pusher reconnect, when the tab becomes visible again, and as a
  // polling fallback while the socket is down — so a call missed while the
  // WebSocket was dead still surfaces.
  const syncActiveCall = useCallback(() => {
    if (!isInterpreter) return;
    Api.getActiveVriCall()
      .then((res) => {
        if (res?.status === 200 && res?.data?.data) {
          const call = res.data.data;
          setCalls((prev) =>
            prev.some((c) => c.id === call.id) ? prev : [call, ...prev]
          );
        }
      })
      .catch(() => {});
  }, [isInterpreter]);

  // Late-login recovery: check for any active call on mount
  useEffect(() => {
    syncActiveCall();
  }, [syncActiveCall]);

  // Pusher subscription + self-healing connection
  useEffect(() => {
    if (!isInterpreter || !interpreterUserId) return;

    const echo = getEcho();
    const pusher = echo.connector.pusher;
    const channelName = `interpreter.${interpreterUserId}`;
    const channel = echo.channel(channelName);

    channel
      .listen('.incoming-call', (data) => {
        if (!data?.callData?.id) return;
        // Deduplicate: ignore if this call is already in the stack
        setCalls((prev) =>
          prev.some((c) => c.id === data.callData.id) ? prev : [data.callData, ...prev]
        );
      })
      .listen('.call-taken', (data) => {
        setCalls((prev) => prev.filter((c) => c.id !== data?.appointmentId));
      })
      .listen('.invite-expired', (data) => {
        setCalls((prev) => prev.filter((c) => c.id !== data?.appointmentId));
      })
      .error((err) => console.warn('[VRI] Pusher channel error:', err));

    let reconnectTimer = null;
    let pollTimer = null;
    let hasConnectedOnce = pusher.connection.state === 'connected';

    const clearTimers = () => {
      if (reconnectTimer) { clearInterval(reconnectTimer); reconnectTimer = null; }
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const onStateChange = ({ current }) => {
      setConnectionState(current);

      if (current === 'connected') {
        clearTimers();
        // A reconnect (not the initial connect) means events may have been lost.
        if (hasConnectedOnce) syncActiveCall();
        hasConnectedOnce = true;
        return;
      }

      if (DOWN_STATES.includes(current)) {
        console.warn(`[VRI] Pusher connection ${current} — will retry every ${RECONNECT_INTERVAL_MS / 1000}s and poll for calls meanwhile`);
        if (!reconnectTimer) {
          reconnectTimer = setInterval(() => {
            if (pusher.connection.state !== 'connected') pusher.connect();
          }, RECONNECT_INTERVAL_MS);
        }
        if (!pollTimer) {
          syncActiveCall();
          pollTimer = setInterval(syncActiveCall, OFFLINE_POLL_INTERVAL_MS);
        }
      }
    };

    // Surfaces quota refusals (4004) and other socket errors that were invisible before.
    const onConnectionError = (err) => console.warn('[VRI] Pusher connection error:', err);

    pusher.connection.bind('state_change', onStateChange);
    pusher.connection.bind('error', onConnectionError);
    // If the socket is already down at mount, start recovery immediately.
    // (The mount-time syncActiveCall above already covers the connected case.)
    setConnectionState(pusher.connection.state);
    if (DOWN_STATES.includes(pusher.connection.state)) {
      onStateChange({ current: pusher.connection.state });
    }

    // Waking a backgrounded/sleeping tab: the socket may have been dead without
    // pusher-js noticing yet (activity timeout is 120s + 30s pong).
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncActiveCall();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      channel.stopListening('.incoming-call');
      channel.stopListening('.call-taken');
      channel.stopListening('.invite-expired');
      pusher.connection.unbind('state_change', onStateChange);
      pusher.connection.unbind('error', onConnectionError);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearTimers();
    };
  }, [isInterpreter, interpreterUserId, syncActiveCall]);

  const removeCall = (id) => setCalls((prev) => prev.filter((c) => c.id !== id));

  const handleAccept = async (callId) => {
    setLoadingId(callId);
    const { data, success, message } = await acceptVriCall(callId);
    setLoadingId(null);
    if (success) {
      setCalls([]);   // clear entire queue — interpreter is now in a call
      dispatch(setInterpreterIframeUrl(data?.iframe_url));
      dispatch(setInterpreterAppid(data?.appointment?.appid));
      navigate(
        `/interpreter/appointments/view/${data.appointment?.id}/join-room-call`,
        { state: { isReplacement: data?.is_replacement === true } }
      );
    } else {
      removeCall(callId);
      toast.error(message || 'Something went wrong', { className: 'errorToast' });
    }
  };

  const handleReject = (callId) => {
    removeCall(callId);
    Api.dismissVriCall(callId);
  };

  // All hooks must run before this conditional return
  const showReconnecting = isInterpreter && DOWN_STATES.includes(connectionState);
  if (isOnVideoCallPage || (calls.length === 0 && !showReconnecting)) return null;

  return (
    <>
      <style>{`
        @keyframes vri_slide_in {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}>
        {showReconnecting && (
          <div
            title="Live call alerts are temporarily unavailable. We keep retrying and checking for calls every 15 seconds."
            style={{
              background: '#fff3cd',
              color: '#856404',
              border: '1px solid #ffc107',
              borderRadius: 10,
              padding: '8px 12px',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: 300,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            }}
          >
            <WifiOff size={16} />
            <span>Reconnecting to call service…</span>
          </div>
        )}
        {calls.map((call) => (
          <CallCard
            key={call.id}
            call={call}
            onAccept={handleAccept}
            onReject={handleReject}
            loadingId={loadingId}
          />
        ))}
      </div>
    </>
  );
};

export default IncomingCalls;
